import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Send, MessageSquareQuote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtData = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT');
};

/**
 * Il canale di ritorno: finora il portale parlava e basta.
 * Quello che il professionista vede coi suoi pazienti torna indietro a noi.
 */
export default function RaccontaCasoPage() {
  const [aree, setAree] = useState([]);
  const [miei, setMiei] = useState([]);
  const [area, setArea] = useState('');
  const [caso, setCaso] = useState('');
  const [esito, setEsito] = useState('');
  const [ricontattabile, setRicontattabile] = useState(false);
  const [invio, setInvio] = useState(false);

  const caricaMiei = () => {
    axios.get(`${API}/me/casi`).then(r => setMiei(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    axios.get(`${API}/application-areas`).then(r => setAree(r.data || [])).catch(() => {});
    caricaMiei();
  }, []);

  const invia = async (e) => {
    e.preventDefault();
    if (caso.trim().length < 30) {
      toast.error('Bastano poche righe, ma servono almeno 30 caratteri.');
      return;
    }
    setInvio(true);
    try {
      await axios.post(`${API}/me/casi`, {
        caso, esito, area_slug: area || null, ricontattabile,
      });
      toast.success('Grazie: il tuo caso è arrivato.');
      setCaso(''); setEsito(''); setArea(''); setRicontattabile(false);
      caricaMiei();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Non è stato possibile inviare il caso');
    } finally {
      setInvio(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="racconta-caso-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Racconta un caso</h1>
        <p className="text-sm text-[#4a4238] mt-2 max-w-2xl leading-relaxed">
          Quello che vedi nella tua pratica è la cosa che ci manca di più. Non serve un
          resoconto formale: com'era la situazione, cosa hai osservato, cosa non ha funzionato.
          Lo leggiamo noi, non viene pubblicato.
        </p>
      </div>

      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={invia} className="space-y-5">
            <div>
              <Label className="text-sm text-[#12100c]">Area interessata (facoltativa)</Label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="caso-area">
                  <SelectValue placeholder="Scegli un'area" />
                </SelectTrigger>
                <SelectContent>
                  {aree.map(a => (
                    <SelectItem key={a.slug} value={a.slug}>{a.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-sm text-[#12100c]">Il caso</Label>
              <Textarea
                value={caso}
                onChange={(e) => setCaso(e.target.value)}
                rows={7}
                maxLength={5000}
                placeholder="Chi avevi davanti, che quadro, cosa hai proposto e perché."
                className="mt-1.5 rounded-xl"
                data-testid="caso-testo"
              />
              <p className="text-[11px] text-[#5b5346] mt-1">{caso.length}/5000</p>
            </div>

            <div>
              <Label className="text-sm text-[#12100c]">Com'è andata (facoltativo)</Label>
              <Textarea
                value={esito}
                onChange={(e) => setEsito(e.target.value)}
                rows={4}
                maxLength={5000}
                placeholder="Cosa hai osservato, in quanto tempo, cosa ti aspettavi e non è successo."
                className="mt-1.5 rounded-xl"
                data-testid="caso-esito"
              />
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="ricontattabile"
                checked={ricontattabile}
                onCheckedChange={(v) => setRicontattabile(v === true)}
                data-testid="caso-ricontattabile"
              />
              <Label htmlFor="ricontattabile" className="text-sm text-[#4a4238] font-normal leading-relaxed">
                Sono disponibile a essere ricontattato su questo caso.
              </Label>
            </div>

            <p className="text-[11px] text-[#5b5346] leading-relaxed">
              Non inserire dati che identificano il paziente: nome, contatti, data di nascita.
              Per quello che ci serve basta il quadro clinico.
            </p>

            <Button type="submit" disabled={invio}
              className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full px-6"
              data-testid="caso-invia">
              {invio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Invia il caso
            </Button>
          </form>
        </CardContent>
      </Card>

      {miei.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-[#12100c] mb-3">
            I casi che hai raccontato
          </h2>
          <div className="space-y-3">
            {miei.map(c => (
              <Card key={c.id} className="rounded-2xl border-gray-100 shadow-sm" data-testid={`mio-caso-${c.id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-1.5 text-xs text-[#5b5346]">
                    <MessageSquareQuote className="w-3.5 h-3.5" />
                    <span>{fmtData(c.created_at)}</span>
                    {c.area_slug && <span>· {c.area_slug.replace(/-/g, ' ')}</span>}
                  </div>
                  <p className="text-sm text-[#4a4238] whitespace-pre-wrap leading-relaxed">{c.caso}</p>
                  {c.esito && (
                    <p className="text-sm text-[#4a4238] whitespace-pre-wrap leading-relaxed mt-2 pt-2 border-t border-gray-100">
                      <span className="text-[#12100c] font-medium">Com'è andata: </span>{c.esito}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
